import { Feature } from 'ol';
import { Stroke, Style, Icon } from 'ol/style';
import { Point, LineString, Geometry } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import { PositionData } from '../services/types/position-service.types';

const _PROJECTION = (<any>window)["appenv"].projection;
const _DASHED_TRACK = 60 * 60 * 1000; // One hour, in milliseconds

export function trackToFeature(positions: PositionData[], lineColor: string, label: string = ""): Feature<Geometry>[] {
    const solidStyle = new Style({
        stroke: new Stroke({
            color: lineColor, width: 2
        })
    });
    const dashedStyle = new Style({
        stroke: new Stroke({
            color: lineColor, width: 2, lineDash: [4,8]
        })
    });

    const features: Feature<Geometry>[] = [];
    let currentLine: Coordinate[] = [];
    const addFeature = function(gap: boolean) {
        const feature = new Feature({
            geometry: new LineString(currentLine),
            name: label.length ? label + (gap ? ' (missing position data)' : '') : null, //text for voyage-ol-map tooltip
            finished: false
        });
        feature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
        feature.setStyle(gap ? dashedStyle : solidStyle);
        feature.set("isGap", gap)
        feature.set('layer', `${label} layer`) // text for new voyage-route-tooltip
        features.push(feature);
    };

    let prev: PositionData;
    positions.filter(p => (p.longitude !== null && p.latitude !== null)).forEach(pos => {
        if (prev) {
            // Compute this line segment, splitting for dateline crossings appropriately
            const segment = datelineSplit([prev.longitude!, prev.latitude!], [pos.longitude!, pos.latitude!]);

            // If there has been a gap in updates on this segment, flush the current line, as we will use a different style for this segment
            const outdated = new Date(pos.timestamp).getTime() - new Date(prev.timestamp).getTime() > _DASHED_TRACK;
            if (outdated && currentLine.length > 1) {
                addFeature(false);
                currentLine = [segment[0]];
            }

            currentLine.push(segment[1]);

            // If the dateline was crossed, split into two features here
            if (segment.length === 4) {
                addFeature(outdated);
                currentLine = [segment[2], segment[3]];
            }

            // If there was a gap in updates, output the dashed line style here
            if (outdated) {
                addFeature(true);
                currentLine = [segment[segment.length - 1]];
            }
        }
        else {
            currentLine.push([pos.longitude!, pos.latitude!]);
        }

        prev = pos;
    })

    if (currentLine.length > 1) {
        addFeature(false);
    }

    return features;
}

function datelineSplit(p1: Coordinate, p2: Coordinate) {
    if (Math.abs(p1[0]-p2[0]) < 180) {
        return [p1, p2];
    }

    const lond1 = 180 - Math.abs(p1[0]);
    const lond2 = 180 - Math.abs(p2[0]);
    const latd = p2[1] - p1[1];
    const lat = p1[1] + (latd * lond1) / (lond1 + lond2);

    const pd1 = [180 * Math.sign(p1[0]), lat];
    const pd2 = [180 * Math.sign(p2[0]), lat];

    return [p1, pd1, pd2, p2];
}